import { ANY_FAMILY, FAMILIES, type Family, type InstalledModelFile, type InstalledModels } from '@couch/shared';

export interface ModelRow { folder: string; file: InstalledModelFile }
export type FolderRows = [folder: string, rows: ModelRow[]][];
export interface FamilyGroup { family: Family; hasBase: boolean; folders: FolderRows; missing: string[] }
export interface ModelGroups { families: FamilyGroup[]; leftover: FolderRows; any: FolderRows; unsure: FolderRows }

const FOLDER_ORDER = ['checkpoints', 'diffusion_models', 'loras', 'vae', 'text_encoders', 'controlnet'];
const BASE_FOLDERS = new Set(['checkpoints', 'diffusion_models']);

function byFolder(rows: ModelRow[]): FolderRows {
  const map = new Map<string, ModelRow[]>();
  for (const row of rows) map.set(row.folder, [...(map.get(row.folder) ?? []), row]);
  const rank = (f: string) => { const i = FOLDER_ORDER.indexOf(f); return i < 0 ? FOLDER_ORDER.length : i; };
  return [...map.entries()].sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
}

/** Parts a split model (diffusion_models, no all-in-one checkpoint) cannot run without. */
function missingParts(family: Family, rows: ModelRow[]): string[] {
  const split = rows.some((r) => r.folder === 'diffusion_models') && !rows.some((r) => r.folder === 'checkpoints');
  if (!split) return [];
  return family.needs
    .filter((need) => !rows.some((r) => (r.file.arch !== null && need.anyOf.includes(r.file.arch)) || (r.folder === need.folder && r.file.familySource === 'manual')))
    .map((need) => need.label);
}

/**
 * Installed models by ecosystem (doc 48): families with a base model first, then families with parts of their own
 * (a LoRA without its model), then shared parts no shown family uses, any model, not sure. A shared VAE alone does
 * not open a group for every family that could use it.
 */
export function groupModels(models: InstalledModels): ModelGroups {
  const rows: ModelRow[] = Object.entries(models.folders).flatMap(([folder, files]) => files.map((file) => ({ folder, file })));
  const families = FAMILIES.flatMap((family): FamilyGroup[] => {
    const own = rows.filter((r) => r.file.families.includes(family.id));
    const hasBase = own.some((r) => BASE_FOLDERS.has(r.folder));
    if (!hasBase && !own.some((r) => r.file.families.length === 1)) return [];
    return [{ family, hasBase, folders: byFolder(own), missing: missingParts(family, own) }];
  });
  const shown = new Set(families.map((g) => g.family.id as string));
  return {
    families: [...families.filter((g) => g.hasBase), ...families.filter((g) => !g.hasBase)],
    leftover: byFolder(rows.filter((r) => r.file.families.length > 0 && !r.file.families.some((f) => f === ANY_FAMILY || shown.has(f)))),
    any: byFolder(rows.filter((r) => r.file.families.includes(ANY_FAMILY))),
    unsure: byFolder(rows.filter((r) => r.file.families.length === 0)),
  };
}
